import type { Domino, GameAction, GameState, LedSuit } from '../types';
import type { GameRules } from '../layers/types';
import type { ExecutionContext } from '../types/execution';
import { executeAction, type ExecuteActionOptions } from '../core/actions';
import { calculateTrickPoints } from '../core/scoring';
import { getPartner } from '../layers/helpers';
import { createDominoes, getDominoPoints } from '../core/dominoes';
import { selectNearEqualWithMetadata } from './near-equal-select';
import type { RandomGenerator } from './hand-sampler';

/** Partner lead strength derived from legal continuations of the current trick. */
export type PartnerSafetyClassification = 'none' | 'current' | 'probable' | 'secure';

/** Where the points in the candidate's current trick can be scored. */
export type CountDestination =
  | 'none'
  | 'partner'
  | 'acting-seat'
  | 'opponent'
  | 'mixed'
  | 'unknown';

/** A candidate shape accepted by the contextual near-equal selector. */
export interface PartnerSafetyCandidate {
  partnerSafety: PartnerSafetyClassification;
  overtakesPartner: boolean;
}

/** Whether a discard candidate spends or keeps a live boss domino. */
export type BossPreservation = 'spends' | 'keeps';

/** Safe, action-bound context for one hypothetical legal play. */
export interface PartnerSafetyAssessment extends PartnerSafetyCandidate {
  /** The partner leads the trick immediately after this candidate. */
  partnerCurrent: boolean;
  /** Share of legal one-trick continuations won by the partner. */
  partnerWinRate: number;
  /** The acting seat wins every legal completed-trick continuation in one sampled world. */
  candidateWinsTrick: boolean;
  /** Destination of the current trick's points across legal continuations. */
  countDestination: CountDestination;
  /** Count value of the domino released by this candidate. */
  countRisk: number;
  /** The candidate is the highest unplayed domino in its own suit (engine suit/trump algebra). */
  liveStanding: boolean;
  /** Whether the candidate spends or keeps a live boss domino. */
  bossPreservation: BossPreservation;
  /** The candidate is a live boss and a lower-ranked same-suit domino remains unplayed outside this hand (leading it pulls tiles). */
  likelyFutureLeadValue: boolean;
  /** Retained count that has a plausible promotion route after this play. */
  protectedCountValue: number;
  /** Share of retained count supported by a promotion route. */
  promotionSupport: number;
  /** Share of legal current-trick completions won by the acting team. */
  leadAccess: number;
  /** Share of team-winning completions that immediately open a promotion suit. */
  remainingControl: number;
  /** Bounded sampled proxy for the chance that the team can cash the protected count. */
  cashability: number;
  /**
   * Unconditional joint share where the family contains a secure count
   * donation, this candidate is secure, and the completion settles the
   * contract for the acting team. A false family gate contributes zero.
   */
  contractSecuringRate: number;
  /** Protected count value released by this play. */
  sacrificedProtectedCountValue: number;
  /** The acting team retains at least one trump after this lead. Absent when following. */
  retainedTrumpControl?: boolean;
  /** This lead does not force the partner to spend its sole remaining trump. Absent when following. */
  protectsPartnerTrumpControl?: boolean;
  /** This defensive trump lead forces the bidder to spend its final trump. Absent when following. */
  leadPressure?: boolean;
  /** Public components of the acting seat's delayed trump re-entry plan. */
  actingSeatRetainedTrump?: boolean;
  externalVoidRuffOpportunity?: boolean;
  retainedUsefulContinuation?: boolean;
  weakOffCleanup?: boolean;
  reentryPlanValue?: number;
}

/** Public, deterministic reason used by contextual selection and receipts. */
export type ContextualControlOutcome =
  | 'partner-safety'
  | 'overtake-avoidance'
  | 'secure-contract-contribution'
  | 'protected-count'
  | 'future-control'
  | 'first-safe-count'
  | 'partnership-control'
  | 'lead-pressure'
  | 'trump-reentry'
  | 'current-trick-control'
  | 'count-risk'
  | 'equal';

export interface ContextualControlComparison {
  comparison: number;
  outcome: ContextualControlOutcome;
}

const SIMULATION_OPTIONS: ExecuteActionOptions = { skipHistory: true, skipValidation: true };
const PROBABLE_WIN_RATE = 0.5;
const RATE_EPSILON = 1e-9;

interface TerminalTrickOutcome {
  winner: number;
  count: number;
  state: GameState;
}

interface TerminalTrickCursor {
  /** Cached terminals in stable legal-action DFS order. */
  readonly cached: readonly TerminalTrickOutcome[];
  /** Prove every terminal has this winner, stopping at the first witness. */
  everyWinner(player: number): boolean;
  /** Resume the same DFS and return the complete terminal set. */
  exhaust(): readonly TerminalTrickOutcome[];
}

/** Return the contextual rank used by both rollout ordering and root choice. */
export function partnerSafetyRank(classification: PartnerSafetyClassification): number {
  switch (classification) {
    case 'secure':
      return 3;
    case 'probable':
      return 2;
    case 'current':
      return 1;
    case 'none':
      return 0;
  }
}

/**
 * Prefer a stronger partner lead, then avoid an overtake.
 * Contract utility remains the caller's primary comparison.
 */
export function comparePartnerSafety(
  left: PartnerSafetyCandidate,
  right: PartnerSafetyCandidate,
): number {
  const safetyDifference = partnerSafetyRank(left.partnerSafety) - partnerSafetyRank(right.partnerSafety);
  if (safetyDifference !== 0) return safetyDifference;
  if (left.overtakesPartner !== right.overtakesPartner) {
    return left.overtakesPartner ? -1 : 1;
  }
  return 0;
}

/**
 * Discard assessment extensions consumed by future-control comparisons.
 * Only meaningful when the candidate does not profitably win the trick.
 */
export interface DiscardCandidate extends PartnerSafetyCandidate {
  /** The acting seat wins every legal completed-trick continuation in one sampled world. */
  candidateWinsTrick: boolean;
  countRisk: number;
  liveStanding: boolean;
  bossPreservation: BossPreservation;
  likelyFutureLeadValue: boolean;
  countDestination?: CountDestination;
  protectedCountValue?: number;
  promotionSupport?: number;
  leadAccess?: number | boolean;
  remainingControl?: number | boolean;
  cashability?: number;
  contractSecuringRate?: number;
  sacrificedProtectedCountValue?: number;
  actingSeatRetainedTrump?: boolean;
  externalVoidRuffOpportunity?: boolean;
  retainedUsefulContinuation?: boolean;
  weakOffCleanup?: boolean;
  reentryPlanValue?: number;
}

/** Post-action count plan consumed by rollout ordering and root selection. */
export interface ProtectedCountPlan {
  protectedCountValue: number;
  promotionSupport: number;
  leadAccess: number;
  remainingControl: number;
  cashability: number;
  contractSecuringRate: number;
  sacrificedProtectedCountValue: number;
}

/**
 * Lead-control fields come from one sampled full world. They are aggregate
 * evidence at the root, never a read of the live hidden hand.
 */
export interface TrumpControlCandidate {
  retainedTrumpControl?: boolean;
  protectsPartnerTrumpControl?: boolean;
  leadPressure?: boolean;
}

/**
 * Preserve the partner's final trump and the team's remaining trump before a
 * defensive lead can claim pressure. Pressure means every legal bidder reply
 * spends that bidder's final trump. Missing fields are inapplicable (such as a
 * follow), so they have no preference.
 */
export function compareTrumpControl(
  left: TrumpControlCandidate,
  right: TrumpControlCandidate,
): number {
  if (
    left.protectsPartnerTrumpControl !== undefined &&
    right.protectsPartnerTrumpControl !== undefined &&
    left.protectsPartnerTrumpControl !== right.protectsPartnerTrumpControl
  ) {
    return left.protectsPartnerTrumpControl ? 1 : -1;
  }
  if (
    left.retainedTrumpControl !== undefined &&
    right.retainedTrumpControl !== undefined &&
    left.retainedTrumpControl !== right.retainedTrumpControl
  ) {
    return left.retainedTrumpControl ? 1 : -1;
  }
  if (left.leadPressure !== undefined && right.leadPressure !== undefined &&
      left.leadPressure !== right.leadPressure) {
    return left.leadPressure ? 1 : -1;
  }
  return 0;
}

export type ContextualCountControlCandidate = Partial<Pick<
  DiscardCandidate,
  | 'partnerSafety'
  | 'overtakesPartner'
  | 'countDestination'
  | 'countRisk'
  | 'protectedCountValue'
  | 'leadAccess'
  | 'cashability'
>>;

function safePartnerCountValue(candidate: ContextualCountControlCandidate): number {
  return candidate.partnerSafety === 'secure' &&
    candidate.countDestination === 'partner' &&
    !candidate.overtakesPartner
    ? candidate.countRisk ?? 0
    : 0;
}

function cashableProtectedCountValue(candidate: ContextualCountControlCandidate): number {
  return Math.max(0, candidate.protectedCountValue ?? 0) * Math.max(0, candidate.cashability ?? 0);
}

/**
 * Count is unsecured only when its sampled destination is not under team
 * control. Team lead access reduces that exposure by the measured share of
 * terminal outcomes won by the acting team. Missing evidence stays fully at
 * risk. Other destinations have no opponent-release risk at this layer.
 */
function unsecuredCountRisk(candidate: ContextualCountControlCandidate): number {
  const countRisk = candidate.countRisk ?? 0;
  if (countRisk <= 0) return 0;
  const leadAccess = Number(candidate.leadAccess ?? 0);
  return (candidate.countDestination === 'opponent' ||
    candidate.countDestination === 'mixed' ||
    candidate.countDestination === 'unknown')
    ? countRisk * (1 - leadAccess)
    : 0;
}

export type ContextualCountControl = Readonly<{
  safePartnerCount: number;
  cashableRetainedCount: number;
  unsecuredCountRisk: number;
  value: number;
}>;

/**
 * One count-control value balances a safe contribution and retained count
 * against a count delivery that remains unsecured. Every caller that needs
 * the count policy uses these same public aggregate terms.
 */
export function assessContextualCountControl(candidate: ContextualCountControlCandidate): ContextualCountControl {
  const safePartnerCount = safePartnerCountValue(candidate);
  const cashableRetainedCount = cashableProtectedCountValue(candidate);
  const unsecuredRisk = unsecuredCountRisk(candidate);
  return {
    safePartnerCount,
    cashableRetainedCount,
    unsecuredCountRisk: unsecuredRisk,
    value: safePartnerCount + cashableRetainedCount - unsecuredRisk,
  };
}

/**
 * Resolve one paired-confidence family in the same order everywhere.
 * Contract utility is deliberately absent: callers form that family first.
 */
export function compareContextualControlDetailed(
  left: DiscardCandidate & TrumpControlCandidate,
  right: DiscardCandidate & TrumpControlCandidate,
): ContextualControlComparison {
  const safetyComparison = comparePartnerSafety(left, right);
  if (safetyComparison !== 0) {
    return {
      comparison: safetyComparison,
      outcome: left.partnerSafety === right.partnerSafety ? 'overtake-avoidance' : 'partner-safety',
    };
  }

  const secureContractComparison = compareSecureContractContribution(left, right);
  if (secureContractComparison !== 0) {
    return { comparison: secureContractComparison, outcome: 'secure-contract-contribution' };
  }

  const leftCountControl = assessContextualCountControl(left);
  const rightCountControl = assessContextualCountControl(right);
  const countControlComparison = leftCountControl.value - rightCountControl.value;
  if (countControlComparison !== 0) {
    const positiveCountPlanComparison =
      (leftCountControl.safePartnerCount + leftCountControl.cashableRetainedCount) -
      (rightCountControl.safePartnerCount + rightCountControl.cashableRetainedCount);
    const riskChangesWinner =
      positiveCountPlanComparison === 0 ||
      Math.sign(countControlComparison) !== Math.sign(positiveCountPlanComparison);
    if (riskChangesWinner) {
      return { comparison: countControlComparison, outcome: 'count-risk' };
    }
    const winningSideHasMoreSafeCount =
      (countControlComparison > 0 && leftCountControl.safePartnerCount > rightCountControl.safePartnerCount) ||
      (countControlComparison < 0 && leftCountControl.safePartnerCount < rightCountControl.safePartnerCount);
    return {
      comparison: countControlComparison,
      outcome: winningSideHasMoreSafeCount ? 'first-safe-count' : 'protected-count',
    };
  }

  if (left.bossPreservation !== right.bossPreservation) {
    return {
      comparison: left.bossPreservation === 'keeps' ? 1 : -1,
      outcome: 'future-control',
    };
  }
  if (left.likelyFutureLeadValue !== right.likelyFutureLeadValue) {
    return {
      comparison: left.likelyFutureLeadValue ? -1 : 1,
      outcome: 'future-control',
    };
  }

  const trumpControlComparison = compareTrumpControl(left, right);
  if (trumpControlComparison !== 0) {
    const partnershipDifference =
      left.protectsPartnerTrumpControl !== right.protectsPartnerTrumpControl ||
      left.retainedTrumpControl !== right.retainedTrumpControl;
    return {
      comparison: trumpControlComparison,
      outcome: partnershipDifference ? 'partnership-control' : 'lead-pressure',
    };
  }

  if ((left.reentryPlanValue ?? 0) !== (right.reentryPlanValue ?? 0)) {
    return {
      comparison: (left.reentryPlanValue ?? 0) - (right.reentryPlanValue ?? 0),
      outcome: 'trump-reentry',
    };
  }

  if (left.candidateWinsTrick !== right.candidateWinsTrick) {
    return {
      comparison: left.candidateWinsTrick ? 1 : -1,
      outcome: 'current-trick-control',
    };
  }

  return { comparison: 0, outcome: 'equal' };
}

export function compareContextualControl(
  left: DiscardCandidate & TrumpControlCandidate,
  right: DiscardCandidate & TrumpControlCandidate,
): number {
  return compareContextualControlDetailed(left, right).comparison;
}

/** Compare each secure candidate's independently measured contract settlement rate. */
export function compareSecureContractContribution(
  left: DiscardCandidate,
  right: DiscardCandidate,
): number {
  if (left.partnerSafety !== 'secure' || right.partnerSafety !== 'secure') return 0;
  const leftRate = left.contractSecuringRate ?? 0;
  const rightRate = right.contractSecuringRate ?? 0;
  return leftRate - rightRate;
}

/**
 * Prefer the stronger post-action count plan when partner control is not
 * secure. A secure partner contribution remains governed by the existing
 * partner-safety and count-destination fields.
 */
export function futureControlDescription(
  candidate: DiscardCandidate,
): string {
  if (candidate.countRisk > 0 && candidate.liveStanding) {
    return 'spent a count boss';
  }
  if (candidate.liveStanding) {
    return 'lost a future boss';
  }
  if (candidate.countRisk > 0 && candidate.partnerSafety === 'secure') {
    return 'released count to a secure partner lead';
  }
  if (candidate.countRisk > 0) {
    return 'released count';
  }
  return 'no future control lost';
}

/**
 * Select a legal play inside the existing contract-utility band.
 * Resolution order: shared contextual control → seeded style.
 * Contextual preference never enlarges the utility band or beats a clear winner.
 */
/** Select from one exact candidate family without rebuilding a score band. */
export function selectContextualFamily<
  T extends PartnerSafetyCandidate & DiscardCandidate & TrumpControlCandidate,
>(
  candidates: Array<{ item: T; score: number }>,
  temperature: number,
  rng: RandomGenerator,
): { item: T; preferredCandidateCount: number } {
  if (candidates.length === 0) {
    throw new Error('selectContextualFamily: no candidates');
  }

  const preferred = candidates.reduce<Array<{ item: T; score: number }>>((best, candidate) => {
    if (best.length === 0) return [candidate];
    const controlComparison = compareContextualControl(candidate.item, best[0]!.item);
    if (controlComparison > 0) return [candidate];
    if (controlComparison === 0) return [...best, candidate];
    return best;
  }, []);

  const selected = selectNearEqualWithMetadata(
    preferred.map(({ item, score }) => ({ item, score })),
    Number.POSITIVE_INFINITY,
    temperature,
    rng,
  );

  return {
    item: selected.item,
    preferredCandidateCount: preferred.length,
  };
}

type PlayAction = Extract<GameAction, { type: 'play' }>;

interface PreparedProtectedCountTopology {
  protectedCountValue: number;
  promotionSupport: number;
  routes: PromotionRoute[];
  sacrificedProtectedCountValue: number;
}

interface PreparedPlayCandidate {
  action: PlayAction;
  futureControl: Pick<
    DiscardCandidate,
    'countRisk' | 'liveStanding' | 'bossPreservation' | 'likelyFutureLeadValue'
  >;
  protectedCount(): PreparedProtectedCountTopology;
  reentryPlan(): ReentryPlan;
}

interface ReentryPlan {
  actingSeatRetainedTrump: boolean;
  externalVoidRuffOpportunity: boolean;
  retainedUsefulContinuation: boolean;
  weakOffCleanup: boolean;
  value: number;
}

interface CheapPlayFacts {
  outcomes: TerminalTrickCursor;
  partnerCurrent: boolean;
  overtakesPartner: boolean;
}

/** One sampled world's lazy view of a prepared legal-play family. */
export interface ContextualPlayWorld {
  /** Lazy records used by rollout ordering through the shared comparator. */
  readonly assessments: readonly PartnerSafetyAssessment[];
  /** Force every receipt-grade field for root aggregation. */
  materializeAll(): PartnerSafetyAssessment[];
}

/**
 * Stable contextual topology for one legal action family.
 *
 * It contains only the acting hand, played tiles, and composed rule facts, so
 * the root can reuse it across hidden-world rotations. Each world still owns
 * its legal trick continuations and hidden-hand-dependent cash routes.
 */
export interface PreparedContextualPlayFamily {
  readonly futureControl: readonly Pick<
    DiscardCandidate,
    'countRisk' | 'liveStanding' | 'bossPreservation' | 'likelyFutureLeadValue'
  >[];
  forState(state: GameState, checkBudget?: () => void): ContextualPlayWorld;
}

/** Prepare action-stable context once for every original count tile. */
export function prepareContextualPlayFamily(
  referenceState: GameState,
  actions: readonly GameAction[],
  ctx: ExecutionContext,
): PreparedContextualPlayFamily {
  const plays = actions.map((action): PlayAction => {
    if (action.type !== 'play') {
      throw new Error('prepareContextualPlayFamily: expected play actions');
    }
    return action;
  });
  const actingPlayer = plays[0]?.player ?? referenceState.currentPlayer;
  if (plays.some((action) => action.player !== actingPlayer)) {
    throw new Error('prepareContextualPlayFamily: actions must share one acting player');
  }

  const actingHand = referenceState.players[actingPlayer]?.hand ?? [];
  const futureControlTopology = buildFutureControlTopology(referenceState, actingPlayer);
  const usesProtectedCount =
    ctx.rules.usesTrickPointsForContract(referenceState) &&
    ctx.rules.usesConventionalSuitControl(referenceState);
  let routeIndex: Map<string, LedSuit[]> | undefined;
  const getRouteIndex = (): Map<string, LedSuit[]> => {
    if (routeIndex !== undefined) return routeIndex;
    routeIndex = new Map<string, LedSuit[]>();
    if (!usesProtectedCount) return routeIndex;
    for (const domino of actingHand) {
      if (getDominoPoints(domino) === 0) continue;
      routeIndex.set(
        String(domino.id),
        promotionRoutes(
          domino,
          referenceState,
          ctx,
          futureControlTopology.played,
          futureControlTopology.ownHand,
        ),
      );
    }
    return routeIndex;
  };

  const prepared = plays.map((action): PreparedPlayCandidate => {
    let protectedCount: PreparedProtectedCountTopology | undefined;
    let reentryPlan: ReentryPlan | undefined;
    const getProtectedCount = (): PreparedProtectedCountTopology => {
      if (protectedCount !== undefined) return protectedCount;
      const routesByDomino = getRouteIndex();
      const retainedCount = usesProtectedCount
        ? actingHand.filter(
            (domino) => String(domino.id) !== action.dominoId && getDominoPoints(domino) > 0,
          )
        : [];
      const retainedCountValue = retainedCount.reduce(
        (sum, domino) => sum + getDominoPoints(domino),
        0,
      );
      const routes = retainedCount.flatMap((domino) =>
        (routesByDomino.get(String(domino.id)) ?? []).map((ledSuit) => ({ domino, ledSuit })),
      );
      const protectedIds = new Set(routes.map(({ domino }) => String(domino.id)));
      const protectedCountValue = retainedCount.reduce(
        (sum, domino) =>
          sum + (protectedIds.has(String(domino.id)) ? getDominoPoints(domino) : 0),
        0,
      );
      const playedDomino = actingHand.find((domino) => String(domino.id) === action.dominoId);
      protectedCount = {
        protectedCountValue,
        promotionSupport: retainedCountValue > 0 ? protectedCountValue / retainedCountValue : 0,
        routes,
        sacrificedProtectedCountValue:
          playedDomino !== undefined &&
          getDominoPoints(playedDomino) > 0 &&
          (routesByDomino.get(action.dominoId)?.length ?? 0) > 0
            ? getDominoPoints(playedDomino)
            : 0,
      };
      return protectedCount;
    };

    return {
      action,
      futureControl: assessFutureControlFromTopology(
        referenceState,
        action,
        ctx,
        futureControlTopology,
      ),
      protectedCount: getProtectedCount,
      reentryPlan: () => {
        if (reentryPlan !== undefined) return reentryPlan;
        const postActionOwnHand = new Set(futureControlTopology.ownHand);
        postActionOwnHand.delete(action.dominoId);
        const postActionTopology: FutureControlTopology = {
          played: new Set([...futureControlTopology.played, action.dominoId]),
          ownHand: postActionOwnHand,
        };
        const hasControlledContinuation = (domino: Domino): boolean => {
          const ledSuit = ctx.rules.getLedSuit(referenceState, domino);
          const rank = ctx.rules.rankInTrick(referenceState, ledSuit, domino);
          return ALL_DOMINOES.every((other) => {
            const id = String(other.id);
            return id === String(domino.id) ||
              postActionTopology.played.has(id) ||
              !ctx.rules.canFollow(referenceState, ledSuit, other) ||
              ctx.rules.rankInTrick(referenceState, ledSuit, other) <= rank ||
              postActionTopology.ownHand.has(id);
          });
        };
        const retainedNonTrumpContinuation = actingHand.some((domino) =>
          String(domino.id) !== action.dominoId &&
          !ctx.rules.isTrump(referenceState, domino) &&
          hasControlledContinuation(domino),
        );
        const playedDomino = actingHand.find((domino) => String(domino.id) === action.dominoId);
        const externalVoidRuffOpportunity = ALL_DOMINOES.some((external) => {
          const id = String(external.id);
          if (postActionTopology.played.has(id) || postActionTopology.ownHand.has(id)) return false;
          if (ctx.rules.isTrump(referenceState, external)) return false;
          const ledSuit = ctx.rules.getLedSuit(referenceState, external);
          return actingHand
            .filter((domino) => String(domino.id) !== action.dominoId)
            .every((domino) => !ctx.rules.canFollow(referenceState, ledSuit, domino));
        });
        const actingSeatRetainedTrump =
          playedDomino !== undefined &&
          !ctx.rules.isTrump(referenceState, playedDomino) &&
          countTrump(actingHand, referenceState, ctx) === 1;
        const retainedUsefulContinuation =
          getProtectedCount().protectedCountValue > 0 || retainedNonTrumpContinuation;
        const weakOffCleanup = playedDomino !== undefined &&
          getDominoPoints(playedDomino) === 0 &&
          !hasControlledContinuation(playedDomino);
        reentryPlan = {
          actingSeatRetainedTrump,
          externalVoidRuffOpportunity,
          retainedUsefulContinuation,
          weakOffCleanup,
          value: actingSeatRetainedTrump &&
            externalVoidRuffOpportunity &&
            retainedUsefulContinuation &&
            weakOffCleanup
            ? 1
            : 0,
        };
        return reentryPlan;
      },
    };
  });

  const family: PreparedContextualPlayFamily = {
    futureControl: prepared.map(({ futureControl }) => futureControl),
    forState: (state, checkBudget) =>
      createContextualPlayWorld(state, prepared, actingPlayer, ctx, checkBudget),
  };
  return family;
}

function createContextualPlayWorld(
  state: GameState,
  prepared: readonly PreparedPlayCandidate[],
  actingPlayer: number,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): ContextualPlayWorld {
  checkBudget?.();
  const partner = getPartner(actingPlayer);
  const partnerSitsOut = isSittingOutPartner(state, partner);
  const winnerBefore = winnerForCurrentTrick(state, ctx);

  // Build every immediate descriptor before comparison. Each candidate owns a
  // stable, resumable completion cursor; sort order can change work, never
  // evidence or legal traversal order.
  const cheapFacts = prepared.map(({ action }): CheapPlayFacts => {
    checkBudget?.();
    const afterCandidate = executeAction(state, action, ctx.rules, SIMULATION_OPTIONS);
    const winnerAfterCandidate = winnerForCurrentTrick(afterCandidate, ctx);
    const partnerCurrent = !partnerSitsOut && winnerAfterCandidate === partner;
    return {
      outcomes: createTerminalTrickCursor(afterCandidate, ctx, checkBudget),
      partnerCurrent,
      overtakesPartner: winnerBefore === partner && winnerAfterCandidate === actingPlayer,
    };
  });

  const partnerWinRates = new Map<number, number>();
  const getPartnerWinRate = (index: number): number => {
    const saved = partnerWinRates.get(index);
    if (saved !== undefined) return saved;
    const outcomes = cheapFacts[index]!.outcomes.exhaust();
    const rate = outcomes.length > 0
      ? outcomes.filter(({ winner }) => winner === partner).length / outcomes.length
      : 0;
    partnerWinRates.set(index, rate);
    return rate;
  };
  const getPartnerSafety = (index: number): PartnerSafetyClassification =>
    cheapFacts[index]!.partnerCurrent
      ? classifyPartnerSafety(true, getPartnerWinRate(index))
      : 'none';
  const getCandidateWinsTrick = (index: number): boolean =>
    cheapFacts[index]!.outcomes.everyWinner(actingPlayer);
  const countDestinations = new Map<number, CountDestination>();
  const getCountDestination = (index: number): CountDestination => {
    const saved = countDestinations.get(index);
    if (saved !== undefined) return saved;
    const destination = classifyCountDestination(
      cheapFacts[index]!.outcomes.exhaust(),
      actingPlayer,
      partner,
    );
    countDestinations.set(index, destination);
    return destination;
  };

  const protectedCashFacts = new Map<
    number,
    Pick<ProtectedCountPlan, 'leadAccess' | 'remainingControl' | 'cashability'>
  >();
  const getProtectedCashFacts = (
    index: number,
  ): Pick<ProtectedCountPlan, 'leadAccess' | 'remainingControl' | 'cashability'> => {
    const saved = protectedCashFacts.get(index);
    if (saved !== undefined) return saved;
    const topology = prepared[index]!.protectedCount();
    const outcomes = cheapFacts[index]!.outcomes.exhaust();
    const teamWinningOutcomes = outcomes.filter(({ winner }) => winner % 2 === actingPlayer % 2);
    const cashableOutcomeCount = teamWinningOutcomes.filter((outcome) =>
      outcomeOpensPromotionRoute(outcome, topology.routes, actingPlayer, ctx),
    ).length;
    const result = {
      leadAccess: outcomes.length > 0 ? teamWinningOutcomes.length / outcomes.length : 0,
      remainingControl: teamWinningOutcomes.length > 0
        ? cashableOutcomeCount / teamWinningOutcomes.length
        : 0,
      cashability: outcomes.length > 0 && topology.protectedCountValue > 0
        ? cashableOutcomeCount / outcomes.length
        : 0,
    };
    protectedCashFacts.set(index, result);
    return result;
  };

  let contractSecuringRates: number[] | undefined;
  const getContractSecuringRates = (): number[] => {
    if (contractSecuringRates !== undefined) return contractSecuringRates;
    contractSecuringRates = prepared.map(() => 0);
    const hasSecureCountDonation = prepared.some(
      ({ futureControl }, index) =>
        futureControl.countRisk > 0 &&
        getPartnerSafety(index) === 'secure' &&
        getCountDestination(index) === 'partner',
    );
    if (!hasSecureCountDonation) {
      return contractSecuringRates;
    }

    const marksBefore = state.teamMarks[actingPlayer % 2] ?? 0;
    const settlementMemo = new WeakMap<GameState, boolean>();
    for (let index = 0; index < prepared.length; index += 1) {
      if (getPartnerSafety(index) !== 'secure') continue;
      const outcomes = cheapFacts[index]!.outcomes.exhaust();
      contractSecuringRates[index] = outcomes.length > 0
        ? outcomes.filter((outcome) =>
            outcomeSecuresContract(outcome, actingPlayer, marksBefore, ctx, settlementMemo),
          ).length / outcomes.length
        : 0;
    }
    return contractSecuringRates;
  };

  const trumpControls = new Map<number, TrumpControlCandidate>();
  const getTrumpControl = (index: number): TrumpControlCandidate => {
    const saved = trumpControls.get(index);
    if (saved !== undefined) return saved;
    const control = assessTrumpControl(state, prepared[index]!.action, ctx, checkBudget);
    trumpControls.set(index, control);
    return control;
  };

  const reentryOptions = new Map<number, number>();
  const getReentryPlanValue = (index: number): number => {
    const saved = reentryOptions.get(index);
    if (saved !== undefined) return saved;
    const action = prepared[index]!.action;
    const actingHand = state.players[actingPlayer]?.hand ?? [];
    const played = actingHand.find((domino) => String(domino.id) === action.dominoId);
    if (played === undefined || ctx.rules.isTrump(state, played)) {
      reentryOptions.set(index, 0);
      return 0;
    }
    const retainedTrumpCount = countTrump(actingHand, state, ctx);
    if (retainedTrumpCount !== 1) {
      reentryOptions.set(index, 0);
      return 0;
    }
    const reentryPlan = prepared[index]!.reentryPlan();
    const canLoseNow = cheapFacts[index]!.outcomes.exhaust().some(
      ({ winner }) => winner % 2 !== actingPlayer % 2,
    );
    const value = canLoseNow ? reentryPlan.value : 0;
    reentryOptions.set(index, value);
    return value;
  };

  const assessments = prepared.map(({ futureControl }, index): PartnerSafetyAssessment => {
    const facts = cheapFacts[index]!;
    const assessment: PartnerSafetyAssessment = {
      get partnerSafety() { return getPartnerSafety(index); },
      partnerCurrent: facts.partnerCurrent,
      get partnerWinRate() { return getPartnerWinRate(index); },
      get candidateWinsTrick() { return getCandidateWinsTrick(index); },
      overtakesPartner: facts.overtakesPartner,
      get countDestination() { return getCountDestination(index); },
      countRisk: futureControl.countRisk,
      liveStanding: futureControl.liveStanding,
      bossPreservation: futureControl.bossPreservation,
      likelyFutureLeadValue: futureControl.likelyFutureLeadValue,
      get protectedCountValue() { return prepared[index]!.protectedCount().protectedCountValue; },
      get promotionSupport() { return prepared[index]!.protectedCount().promotionSupport; },
      get leadAccess() { return getProtectedCashFacts(index).leadAccess; },
      get remainingControl() { return getProtectedCashFacts(index).remainingControl; },
      get cashability() { return getProtectedCashFacts(index).cashability; },
      /**
       * Joint rate: family has a secure count donation AND this secure
       * candidate immediately settles the contract in a legal continuation.
       */
      get contractSecuringRate() { return getContractSecuringRates()[index] ?? 0; },
      get sacrificedProtectedCountValue() {
        return prepared[index]!.protectedCount().sacrificedProtectedCountValue;
      },
      get actingSeatRetainedTrump() { return prepared[index]!.reentryPlan().actingSeatRetainedTrump; },
      get externalVoidRuffOpportunity() { return prepared[index]!.reentryPlan().externalVoidRuffOpportunity; },
      get retainedUsefulContinuation() { return prepared[index]!.reentryPlan().retainedUsefulContinuation; },
      get weakOffCleanup() { return prepared[index]!.reentryPlan().weakOffCleanup; },
      get reentryPlanValue() { return getReentryPlanValue(index); },
    };
    if (state.currentTrick.length === 0) {
      Object.defineProperties(assessment, {
        retainedTrumpControl: {
          enumerable: true,
          get: () => getTrumpControl(index).retainedTrumpControl,
        },
        protectsPartnerTrumpControl: {
          enumerable: true,
          get: () => getTrumpControl(index).protectsPartnerTrumpControl,
        },
        leadPressure: {
          enumerable: true,
          get: () => getTrumpControl(index).leadPressure,
        },
      });
    }
    return assessment;
  });

  const world = {
    assessments,
    materializeAll: () => assessments.map(materializeAssessment),
  };
  return world;
}

function materializeAssessment(assessment: PartnerSafetyAssessment): PartnerSafetyAssessment {
  return {
    partnerSafety: assessment.partnerSafety,
    partnerCurrent: assessment.partnerCurrent,
    partnerWinRate: assessment.partnerWinRate,
    candidateWinsTrick: assessment.candidateWinsTrick,
    overtakesPartner: assessment.overtakesPartner,
    countDestination: assessment.countDestination,
    countRisk: assessment.countRisk,
    liveStanding: assessment.liveStanding,
    bossPreservation: assessment.bossPreservation,
    likelyFutureLeadValue: assessment.likelyFutureLeadValue,
    protectedCountValue: assessment.protectedCountValue,
    promotionSupport: assessment.promotionSupport,
    leadAccess: Number(assessment.leadAccess),
    remainingControl: Number(assessment.remainingControl),
    cashability: assessment.cashability,
    contractSecuringRate: assessment.contractSecuringRate,
    sacrificedProtectedCountValue: assessment.sacrificedProtectedCountValue,
    ...(assessment.reentryPlanValue !== undefined
      ? {
          actingSeatRetainedTrump: assessment.actingSeatRetainedTrump,
          externalVoidRuffOpportunity: assessment.externalVoidRuffOpportunity,
          retainedUsefulContinuation: assessment.retainedUsefulContinuation,
          weakOffCleanup: assessment.weakOffCleanup,
          reentryPlanValue: assessment.reentryPlanValue,
        }
      : {}),
    ...(assessment.retainedTrumpControl !== undefined
      ? { retainedTrumpControl: assessment.retainedTrumpControl }
      : {}),
    ...(assessment.protectsPartnerTrumpControl !== undefined
      ? { protectsPartnerTrumpControl: assessment.protectsPartnerTrumpControl }
      : {}),
    ...(assessment.leadPressure !== undefined ? { leadPressure: assessment.leadPressure } : {}),
  };
}

/** Assess every candidate against the same composed one-trick continuation model. */
export function assessPlayActions(
  state: GameState,
  actions: readonly GameAction[],
  ctx: ExecutionContext,
  checkBudget?: () => void,
): PartnerSafetyAssessment[] {
  return prepareContextualPlayFamily(state, actions, ctx)
    .forState(state, checkBudget)
    .materializeAll();
}

/** Assess one legal play from the acting seat's current state. */
export function assessPlayAction(
  state: GameState,
  action: GameAction,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): PartnerSafetyAssessment {
  return assessPlayActions(state, [action], ctx, checkBudget)[0]!;
}

/**
 * Assess partnership and bidder control for a lead in one complete sampled
 * world. The caller aggregates only booleans across its ordered world set.
 */
export function assessTrumpControl(
  state: GameState,
  action: GameAction,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): TrumpControlCandidate {
  checkBudget?.();
  if (action.type !== 'play') {
    throw new Error('assessTrumpControl: expected a play action');
  }
  if (state.currentTrick.length !== 0) {
    return {};
  }

  const actingHand = state.players[action.player]?.hand ?? [];
  const domino = actingHand.find((candidate) => String(candidate.id) === action.dominoId);
  if (!domino) {
    return {};
  }

  const partner = getPartner(action.player);
  const partnerHand = state.players[partner]?.hand ?? [];
  const isTrumpLead = ctx.rules.isTrump(state, domino);
  const actingTrumpCount = countTrump(actingHand, state, ctx);
  const partnerTrumpCount = countTrump(partnerHand, state, ctx);

  const partnerReplies = repliesAfterLead(state, action, partner, ctx, checkBudget);
  const partnerMustSpendTrump = partnerTrumpCount > 0 &&
    partnerReplies.length > 0 &&
    partnerReplies.every((reply) => replySpendsTrump(state, partner, reply, ctx));
  const actingTrumpRemaining = actingTrumpCount - (isTrumpLead ? 1 : 0);
  const partnerTrumpRemaining = partnerTrumpCount - (partnerMustSpendTrump ? 1 : 0);
  const retainedTrumpControl = actingTrumpRemaining + partnerTrumpRemaining > 0;
  const protectsPartnerTrumpControl = partnerTrumpCount !== 1 || !partnerMustSpendTrump;

  const bidder = state.winningBidder;
  const bidderTrumpCount = bidder >= 0
    ? countTrump(state.players[bidder]?.hand ?? [], state, ctx)
    : 0;
  const bidderReplies = bidder >= 0
    ? repliesAfterLead(state, action, bidder, ctx, checkBudget)
    : [];
  const leadPressure =
    isTrumpLead &&
    bidder >= 0 &&
    bidder % 2 !== action.player % 2 &&
    bidderTrumpCount === 1 &&
    bidderReplies.length > 0 &&
    bidderReplies.every((reply) => replySpendsTrump(state, bidder, reply, ctx));

  return { retainedTrumpControl, protectsPartnerTrumpControl, leadPressure };
}

/**
 * Find a target seat's legal choices after a lead without assuming the target
 * is next to act. Earlier seats are advanced through their real legal actions.
 */
function repliesAfterLead(
  state: GameState,
  action: GameAction,
  target: number,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): GameAction[] {
  checkBudget?.();
  const afterLead = executeAction(state, action, ctx.rules, SIMULATION_OPTIONS);
  return collectSeatReplies(afterLead, target, ctx, checkBudget);
}

function collectSeatReplies(
  state: GameState,
  target: number,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): GameAction[] {
  const replies: GameAction[] = [];
  const walk = (working: GameState) => {
    checkBudget?.();
    if (working.currentPlayer === target) {
      for (const domino of ctx.rules.getValidPlays(working, working.currentPlayer)) {
        replies.push({
          type: 'play',
          player: working.currentPlayer,
          dominoId: String(domino.id),
        });
      }
      return;
    }
    if (working.currentTrick.length === 0 || ctx.rules.isTrickComplete(working)) return;
    for (const domino of ctx.rules.getValidPlays(working, working.currentPlayer)) {
      const undo = pushLegalPlay(working, ctx.rules, domino);
      walk(working);
      undo();
    }
  };
  walk(cloneForTrickSearch(state));
  return replies;
}

function replySpendsTrump(
  state: GameState,
  player: number,
  reply: GameAction,
  ctx: ExecutionContext,
): boolean {
  if (reply.type !== 'play') return false;
  const domino = state.players[player]?.hand.find(
    (candidate) => String(candidate.id) === reply.dominoId,
  );
  return domino !== undefined && ctx.rules.isTrump(state, domino);
}

function countTrump(dominoes: readonly Domino[], state: GameState, ctx: ExecutionContext): number {
  return dominoes.filter((domino) => ctx.rules.isTrump(state, domino)).length;
}

/**
 * Assess the future-control fields of one legal play.
 *
 * The four fields depend only on played dominoes, the acting hand, and trump,
 * so they are identical across sampled hidden worlds. This function is pure
 * and exported so callers can compute each candidate's future control once per
 * decision instead of once per sampled world.
 */
export function assessFutureControl(
  state: GameState,
  action: GameAction,
  ctx: ExecutionContext,
): Pick<
  DiscardCandidate,
  'countRisk' | 'liveStanding' | 'bossPreservation' | 'likelyFutureLeadValue'
> {
  const actingPlayer = action.type === 'play' ? action.player : state.currentPlayer;
  return assessFutureControlFromTopology(
    state,
    action,
    ctx,
    buildFutureControlTopology(state, actingPlayer),
  );
}

interface FutureControlTopology {
  played: ReadonlySet<string>;
  ownHand: ReadonlySet<string>;
}

function buildFutureControlTopology(state: GameState, actingPlayer: number): FutureControlTopology {
  return {
    played: collectPlayedDominoIds(state),
    ownHand: new Set(
      (state.players[actingPlayer]?.hand ?? []).map((domino) => String(domino.id)),
    ),
  };
}

function assessFutureControlFromTopology(
  state: GameState,
  action: GameAction,
  ctx: ExecutionContext,
  topology: FutureControlTopology,
): Pick<
  DiscardCandidate,
  'countRisk' | 'liveStanding' | 'bossPreservation' | 'likelyFutureLeadValue'
> {
  if (action.type !== 'play') {
    throw new Error('assessFutureControl: expected a play action');
  }
  if (!ctx.rules.usesConventionalSuitControl(state)) {
    const result = {
      countRisk: 0,
      liveStanding: false,
      bossPreservation: 'keeps',
      likelyFutureLeadValue: false,
    } as const;
    return result;
  }

  const domino = state.players[action.player]?.hand.find(
    (d) => String(d.id) === action.dominoId,
  );
  if (!domino) {
    const result = {
      countRisk: 0,
      liveStanding: false,
      bossPreservation: 'keeps',
      likelyFutureLeadValue: false,
    } as const;
    return result;
  }

  const countRisk = ctx.rules.usesTrickPointsForContract(state)
    ? getDominoPoints(domino)
    : 0;
  const { liveStanding, hasLowerOutsideHand } = assessSuitControl(domino, state, ctx, topology);
  const bossPreservation: BossPreservation = liveStanding ? 'spends' : 'keeps';
  const likelyFutureLeadValue = liveStanding && hasLowerOutsideHand;

  const result = {
    countRisk,
    liveStanding,
    bossPreservation,
    likelyFutureLeadValue,
  };
  return result;
}

/** Assess a post-action protected-count plan through composed suit algebra. */
export function assessProtectedCountPlan(
  state: GameState,
  action: GameAction,
  ctx: ExecutionContext,
): ProtectedCountPlan {
  const assessment = assessPlayAction(state, action, ctx);
  return {
    protectedCountValue: assessment.protectedCountValue,
    promotionSupport: assessment.promotionSupport,
    leadAccess: Number(assessment.leadAccess),
    remainingControl: Number(assessment.remainingControl),
    cashability: assessment.cashability,
    contractSecuringRate: assessment.contractSecuringRate,
    sacrificedProtectedCountValue: assessment.sacrificedProtectedCountValue,
  };
}

interface PromotionRoute {
  domino: Domino;
  ledSuit: LedSuit;
}

function outcomeOpensPromotionRoute(
  outcome: TerminalTrickOutcome,
  routes: readonly PromotionRoute[],
  actingPlayer: number,
  ctx: ExecutionContext,
): boolean {
  if (outcome.winner % 2 !== actingPlayer % 2) {
    return false;
  }
  return routes.some(({ domino, ledSuit }) => {
    if (outcome.winner === actingPlayer) {
      return ctx.rules.getLedSuit(outcome.state, domino) === ledSuit;
    }
    const winnerHand = outcome.state.players[outcome.winner]?.hand ?? [];
    const protectedRank = ctx.rules.rankInTrick(outcome.state, ledSuit, domino);
    return winnerHand.some((leadDomino) =>
      ctx.rules.getLedSuit(outcome.state, leadDomino) === ledSuit &&
      ctx.rules.rankInTrick(outcome.state, ledSuit, leadDomino) < protectedRank);
  });
}

function outcomeSecuresContract(
  outcome: TerminalTrickOutcome,
  actingPlayer: number,
  marksBefore: number,
  ctx: ExecutionContext,
  memo: WeakMap<GameState, boolean>,
): boolean {
  const saved = memo.get(outcome.state);
  if (saved !== undefined) return saved;
  const completed = executeAction(
    outcome.state,
    { type: 'complete-trick' },
    ctx.rules,
    SIMULATION_OPTIONS,
  );
  const secured =
    completed.phase === 'scoring' &&
    (ctx.rules.calculateScore(completed)[actingPlayer % 2] ?? marksBefore) > marksBefore;
  memo.set(outcome.state, secured);
  return secured;
}

/** Classify current, probable, and secure from a public lead and legal share. */
export function classifyPartnerSafety(
  partnerCurrent: boolean,
  partnerWinRate: number,
): PartnerSafetyClassification {
  if (!partnerCurrent) return 'none';
  if (partnerWinRate >= 1 - RATE_EPSILON) return 'secure';
  if (partnerWinRate > PROBABLE_WIN_RATE) return 'probable';
  return 'current';
}

/** Aggregate current-trick count destinations across sampled worlds. */
export function aggregateCountDestination(
  destinations: readonly CountDestination[],
): CountDestination {
  if (destinations.length === 0) return 'unknown';
  if (destinations.every((destination) => destination === 'none')) return 'none';
  if (destinations.some((destination) => destination === 'mixed')) return 'mixed';

  const hasNoCount = destinations.some((destination) => destination === 'none');
  const known = new Set(
    destinations.filter(
      (destination): destination is Exclude<CountDestination, 'none' | 'mixed' | 'unknown'> =>
        destination !== 'none' && destination !== 'unknown',
    ),
  );
  if (known.size > 1) return 'mixed';
  if (destinations.some((destination) => destination === 'unknown')) {
    return known.size === 0 ? 'unknown' : 'mixed';
  }
  if (hasNoCount && known.size > 0) return 'mixed';
  if (known.size === 0) return 'unknown';
  return known.values().next().value ?? 'unknown';
}

function classifyCountDestination(
  outcomes: readonly TerminalTrickOutcome[],
  actingPlayer: number,
  partner: number,
): CountDestination {
  if (outcomes.length === 0) return 'unknown';

  const destinations = outcomes.map(({ winner, count }): CountDestination => {
    if (count === 0) return 'none';
    if (winner === actingPlayer) {
      return 'acting-seat';
    } else if (winner === partner) {
      return 'partner';
    } else {
      return 'opponent';
    }
  });
  return aggregateCountDestination(destinations);
}

function isSittingOutPartner(state: GameState, partner: number): boolean {
  return state.trump.type === 'nello' && state.winningBidder >= 0 && getPartner(state.winningBidder) === partner;
}

function collectPlayedDominoIds(state: GameState): Set<string> {
  const played = new Set<string>();
  for (const trick of state.tricks) {
    for (const play of trick.plays) {
      played.add(String(play.domino.id));
    }
  }
  for (const play of state.currentTrick) {
    played.add(String(play.domino.id));
  }
  return played;
}

const ALL_DOMINOES = createDominoes();

/**
 * A domino is a live boss iff every unplayed domino that can follow its own
 * led suit (per the engine's suit/trump algebra) has a strictly lower rank.
 * Doubles are the boss of their natural suit; trump dominoes rank within the
 * trump suit. Purely public: (candidate, played-set, trump) — no hidden hands.
 */
function assessSuitControl(
  candidate: Domino,
  state: GameState,
  ctx: ExecutionContext,
  topology: FutureControlTopology,
): { liveStanding: boolean; hasLowerOutsideHand: boolean } {
  const candidateId = String(candidate.id);
  const ledSuit = ctx.rules.getLedSuit(state, candidate);
  const candidateRank = ctx.rules.rankInTrick(state, ledSuit, candidate);
  let hasLowerOutsideHand = false;

  for (const other of ALL_DOMINOES) {
    const id = String(other.id);
    if (id === candidateId || topology.played.has(id)) continue;
    if (!ctx.rules.canFollow(state, ledSuit, other)) continue;
    const rank = ctx.rules.rankInTrick(state, ledSuit, other);
    if (rank > candidateRank) {
      return { liveStanding: false, hasLowerOutsideHand: false };
    }
    if (rank < candidateRank && !topology.ownHand.has(id)) {
      hasLowerOutsideHand = true;
    }
  }
  return { liveStanding: true, hasLowerOutsideHand };
}

/**
 * A domino has a promotion route when every higher unplayed tile in its
 * composed suit is already in the same hand. The hand can spend those
 * protectors before it tries to cash the domino.
 */
function promotionRoutes(
  candidate: Domino,
  state: GameState,
  ctx: ExecutionContext,
  played: ReadonlySet<string>,
  ownHand: ReadonlySet<string>,
): LedSuit[] {
  const candidateId = String(candidate.id);
  const candidateSuits = ([0, 1, 2, 3, 4, 5, 6, 7] as LedSuit[]).filter((ledSuit) =>
    ctx.rules.canFollow(state, ledSuit, candidate));

  const routes = candidateSuits.filter((ledSuit) => {
    const candidateRank = ctx.rules.rankInTrick(state, ledSuit, candidate);
    for (const other of ALL_DOMINOES) {
      const id = String(other.id);
      if (id === candidateId || played.has(id)) continue;
      if (!ctx.rules.canFollow(state, ledSuit, other)) continue;
      if (ctx.rules.rankInTrick(state, ledSuit, other) <= candidateRank) continue;
      if (!ownHand.has(id)) {
        return false;
      }
    }
    return true;
  });
  return routes;
}

function winnerForCurrentTrick(state: GameState, ctx: ExecutionContext): number | null {
  if (state.currentTrick.length === 0) return null;
  return ctx.rules.calculateTrickWinner(state, state.currentTrick);
}

function cloneForTrickSearch(state: GameState): GameState {
  return {
    ...state,
    players: state.players.map((player) => ({
      ...player,
      hand: player.hand.slice(),
    })),
    currentTrick: state.currentTrick.slice(),
  };
}

function snapshotTrickState(state: GameState): GameState {
  return {
    ...state,
    players: state.players.map((player) => ({
      ...player,
      hand: player.hand.slice(),
    })),
    currentTrick: state.currentTrick.slice(),
  };
}

/**
 * Apply a tile already known to be legal. Caller restores with the returned undo.
 * Led suit and next seat are read from the pre-play state, matching executePlay.
 */
function pushLegalPlay(state: GameState, rules: GameRules, domino: Domino): () => void {
  const player = state.currentPlayer;
  const playerState = state.players[player];
  if (!playerState) {
    throw new Error(`pushLegalPlay: missing player ${player}`);
  }
  const hand = playerState.hand;
  const index = (() => {
    const byRef = hand.indexOf(domino);
    if (byRef >= 0) return byRef;
    return hand.findIndex((tile) => tile.id === domino.id);
  })();
  if (index < 0) {
    throw new Error(`pushLegalPlay: ${String(domino.id)} is not in seat ${player}'s hand`);
  }
  const previousSuit = state.currentSuit;
  const previousPlayer = player;
  const nextSuit =
    state.currentTrick.length === 0 ? rules.getLedSuit(state, domino) : state.currentSuit;
  const nextPlayer = rules.getNextPlayer(state, player);
  const played = hand.splice(index, 1)[0]!;
  state.currentTrick.push({ player, domino: played });
  state.currentSuit = nextSuit;
  state.currentPlayer = nextPlayer;
  return () => {
    state.currentPlayer = previousPlayer;
    state.currentSuit = previousSuit;
    state.currentTrick.pop();
    hand.splice(index, 0, played);
  };
}

function createTerminalTrickCursor(
  state: GameState,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): TerminalTrickCursor {
  const iterator = generateTrickOutcomes(state, ctx, checkBudget);
  const cached: TerminalTrickOutcome[] = [];
  const winnerProofs = new Map<number, boolean>();
  let done = false;

  const advance = (): TerminalTrickOutcome | undefined => {
    if (done) return undefined;
    checkBudget?.();
    const next = iterator.next();
    if (next.done) {
      done = true;
      return undefined;
    }
    cached.push(next.value);
    return next.value;
  };

  return {
    cached,
    everyWinner: (player) => {
      const saved = winnerProofs.get(player);
      if (saved !== undefined) return saved;
      for (const outcome of cached) {
        if (outcome.winner !== player) {
          winnerProofs.set(player, false);
          return false;
        }
      }
      while (!done) {
        const outcome = advance();
        if (outcome !== undefined && outcome.winner !== player) {
          winnerProofs.set(player, false);
          return false;
        }
      }
      const result = cached.length > 0;
      winnerProofs.set(player, result);
      return result;
    },
    exhaust: () => {
      while (!done) advance();
      return cached;
    },
  };
}

function* generateTrickOutcomes(
  state: GameState,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): Generator<TerminalTrickOutcome> {
  checkBudget?.();
  if (ctx.rules.isTrickComplete(state)) {
    const winner = winnerForCurrentTrick(state, ctx);
    if (winner !== null) {
      yield { winner, count: calculateTrickPoints(state.currentTrick), state };
    }
    return;
  }

  const working = cloneForTrickSearch(state);
  yield* walkTrickOutcomes(working, ctx, checkBudget);
}

function* walkTrickOutcomes(
  state: GameState,
  ctx: ExecutionContext,
  checkBudget?: () => void,
): Generator<TerminalTrickOutcome> {
  checkBudget?.();
  if (ctx.rules.isTrickComplete(state)) {
    const winner = ctx.rules.calculateTrickWinner(state, state.currentTrick);
    yield {
      winner,
      count: calculateTrickPoints(state.currentTrick),
      state: snapshotTrickState(state),
    };
    return;
  }

  const plays = ctx.rules.getValidPlays(state, state.currentPlayer);
  for (const domino of plays) {
    const undo = pushLegalPlay(state, ctx.rules, domino);
    yield* walkTrickOutcomes(state, ctx, checkBudget);
    undo();
  }
}
